// Pure, framework-agnostic access-badge math. Turns the set of CURRENTLY GRANTED
// capabilities for one plugin into a one-glance summary for the standing Settings
// permissions panel (spec 7.6). No DOM, no React: trivially unit-testable.

import type { Capability } from './permissionBroker';

export type AccessLevel = 'none' | 'limited' | 'broad';

export interface AccessBadge {
  level: AccessLevel;
  label: string;
  detail: string;
}

/**
 * Writing files, spawning processes, network, and version control are the
 * broad-blast capabilities. Git mutates the repository (commits, stages,
 * branch operations), so it sits with the other high-blast grants.
 */
export const BROAD_CAPABILITIES: readonly Capability[] = ['fs.write', 'pty', 'net', 'git'];

/** Plain-language label per capability (badge detail + per-row label). */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  'fs.read': 'Read files',
  'fs.write': 'Write files',
  pty: 'Run programs',
  net: 'Network',
  clipboard: 'Clipboard',
  git: 'Version control',
};

/** Canonical order so the detail string is deterministic regardless of input order. */
const CAPABILITY_ORDER: readonly Capability[] = [
  'fs.read',
  'fs.write',
  'pty',
  'net',
  'clipboard',
  'git',
];

export function computeAccessBadge(granted: Capability[]): AccessBadge {
  const set = new Set(granted);
  const ordered = CAPABILITY_ORDER.filter((cap) => set.has(cap));

  if (ordered.length === 0) {
    return { level: 'none', label: 'No access', detail: 'No capabilities granted' };
  }

  const detail = ordered.map((cap) => CAPABILITY_LABELS[cap]).join(', ');
  const broad = ordered.some((cap) => BROAD_CAPABILITIES.includes(cap));
  return broad
    ? { level: 'broad', label: 'Broad access', detail }
    : { level: 'limited', label: 'Limited access', detail };
}
